import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Award, Target, TrendingUp, AlertCircle, Search, ClipboardList, Sparkles } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-2xl font-bold">{value ?? "—"}</p></div>
      </CardContent>
    </Card>
  );
}

export default function PerformanceDevelopment() {
  const [reviews, setReviews] = useState([]);
  const [goals, setGoals] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState("reviews");

  const [revOpen, setRevOpen] = useState(false);
  const [revEdit, setRevEdit] = useState(null);
  const emptyRev = { employee_id: "", period: "", review_date: "", reviewer: "", overall_rating: "", strengths: "", areas_to_improve: "", comments: "", status: "draft" };
  const [revForm, setRevForm] = useState(emptyRev);

  const [goalOpen, setGoalOpen] = useState(false);
  const [goalEdit, setGoalEdit] = useState(null);
  const emptyGoal = { employee_id: "", title: "", description: "", category: "skill", target_date: "", status: "in_progress", progress: 0 };
  const [goalForm, setGoalForm] = useState(emptyGoal);

  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [rRes, gRes, sRes, eRes] = await Promise.all([
        axios.get(`${API}/performance/reviews`),
        axios.get(`${API}/performance/goals`),
        axios.get(`${API}/performance/stats`),
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
      ]);
      setReviews(rRes.data || []); setGoals(gRes.data || []); setStats(sRes.data || {}); setEmployees(eRes.data || []);
    } catch { toast.error("Failed to load performance data"); }
  };
  useEffect(() => { load(); }, []);

  const empName = (id) => employees.find((x) => x.id === id)?.name || (id ? "Unknown" : "—");

  // Reviews CRUD
  const saveRev = async () => {
    if (!revForm.employee_id || !revForm.period) { toast.error("Employee and Period are required"); return; }
    const payload = { ...revForm };
    if (payload.overall_rating === "" || payload.overall_rating === null) delete payload.overall_rating;
    else payload.overall_rating = Number(payload.overall_rating);
    if (!payload.review_date) delete payload.review_date;
    try {
      if (revEdit) await axios.put(`${API}/performance/reviews/${revEdit.id}`, payload);
      else await axios.post(`${API}/performance/reviews`, payload);
      toast.success(revEdit ? "Review updated" : "Review created");
      setRevOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delRev = async (id) => { if (!window.confirm("Delete this review?")) return;
    try { await axios.delete(`${API}/performance/reviews/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  // Goals CRUD
  const saveGoal = async () => {
    if (!goalForm.employee_id || !goalForm.title.trim()) { toast.error("Employee and Title are required"); return; }
    const payload = { ...goalForm, progress: Number(goalForm.progress || 0) };
    if (!payload.target_date) delete payload.target_date;
    try {
      if (goalEdit) await axios.put(`${API}/performance/goals/${goalEdit.id}`, payload);
      else await axios.post(`${API}/performance/goals`, payload);
      toast.success(goalEdit ? "Goal updated" : "Goal created");
      setGoalOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const delGoal = async (id) => { if (!window.confirm("Delete this goal?")) return;
    try { await axios.delete(`${API}/performance/goals/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  const filteredReviews = reviews.filter((r) => !search || `${empName(r.employee_id)} ${r.period} ${r.reviewer || ""}`.toLowerCase().includes(search.toLowerCase()));
  const filteredGoals = goals.filter((g) => !search || `${empName(g.employee_id)} ${g.title} ${g.category}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Performance &amp; Development</h1>
        <p className="text-sm text-muted-foreground mt-1">Run reviews, track ratings, and drive growth with measurable goals.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Award} label="Total Reviews" value={stats.total_reviews ?? 0} tint="bg-primary/10 text-primary" />
        <StatCard icon={Sparkles} label="Avg Rating" value={stats.avg_rating ?? "—"} tint="bg-amber-500/10 text-amber-600" />
        <StatCard icon={Target} label="Total Goals" value={stats.total_goals ?? 0} tint="bg-blue-500/10 text-blue-600" />
        <StatCard icon={TrendingUp} label="Goals Achieved" value={stats.goals_achieved ?? 0} tint="bg-emerald-500/10 text-emerald-600" />
        <StatCard icon={AlertCircle} label="Goals Overdue" value={stats.goals_overdue ?? 0} tint="bg-red-500/10 text-red-600" />
      </div>

      <div className="flex justify-between items-center">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex justify-between items-center mb-2">
            <TabsList>
              <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
              <TabsTrigger value="goals" data-testid="tab-goals">Goals</TabsTrigger>
            </TabsList>
            <div className="flex gap-2 items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" />
              </div>
              {tab === "reviews" ? (
                <Button onClick={() => { setRevEdit(null); setRevForm(emptyRev); setRevOpen(true); }} data-testid="add-review-btn"><Plus className="h-4 w-4 mr-2" />New Review</Button>
              ) : (
                <Button onClick={() => { setGoalEdit(null); setGoalForm(emptyGoal); setGoalOpen(true); }} data-testid="add-goal-btn"><Plus className="h-4 w-4 mr-2" />New Goal</Button>
              )}
            </div>
          </div>

          <TabsContent value="reviews">
            <Card><CardContent className="p-4">
              {filteredReviews.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground"><ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No performance reviews yet.</p></div>
              ) : (
                <Table><TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Period</TableHead><TableHead>Reviewer</TableHead><TableHead>Rating</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{filteredReviews.map((r) => (
                  <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                    <TableCell className="font-medium">{empName(r.employee_id)}</TableCell>
                    <TableCell>{r.period}</TableCell>
                    <TableCell className="text-sm">{r.reviewer || "—"}</TableCell>
                    <TableCell>{r.overall_rating != null ? <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">{r.overall_rating} / 5</Badge> : "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={r.status === "finalized" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-slate-500/10 text-slate-600 border-slate-500/30"}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => { setRevEdit(r); setRevForm({ ...emptyRev, ...r, overall_rating: r.overall_rating ?? "" }); setRevOpen(true); }} data-testid={`edit-review-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delRev(r.id)} data-testid={`delete-review-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody></Table>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="goals">
            <Card><CardContent className="p-4">
              {filteredGoals.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground"><Target className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No development goals yet.</p></div>
              ) : (
                <Table><TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Goal</TableHead><TableHead>Category</TableHead><TableHead>Target</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{filteredGoals.map((g) => (
                  <TableRow key={g.id} data-testid={`goal-row-${g.id}`}>
                    <TableCell className="font-medium">{empName(g.employee_id)}</TableCell>
                    <TableCell>{g.title}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{g.category}</Badge></TableCell>
                    <TableCell className="text-sm">{g.target_date ? new Date(g.target_date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="w-40"><div className="flex items-center gap-2"><Progress value={g.progress || 0} className="h-2" /><span className="text-xs w-10 text-right">{g.progress || 0}%</span></div></TableCell>
                    <TableCell><Badge variant="outline" className={g.status === "achieved" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : g.status === "missed" ? "bg-red-500/10 text-red-600 border-red-500/30" : "bg-blue-500/10 text-blue-600 border-blue-500/30"}>{g.status.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => { setGoalEdit(g); setGoalForm({ ...emptyGoal, ...g }); setGoalOpen(true); }} data-testid={`edit-goal-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delGoal(g.id)} data-testid={`delete-goal-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody></Table>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Dialog */}
      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{revEdit ? "Edit Review" : "New Performance Review"}</DialogTitle><DialogDescription>Capture period rating and qualitative feedback.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employee *</Label>
                <Select value={revForm.employee_id} onValueChange={(v) => setRevForm({ ...revForm, employee_id: v })}>
                  <SelectTrigger data-testid="review-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Period *</Label><Input value={revForm.period} onChange={(e) => setRevForm({ ...revForm, period: e.target.value })} placeholder="2026-Q1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Review Date</Label><Input type="date" value={revForm.review_date || ""} onChange={(e) => setRevForm({ ...revForm, review_date: e.target.value })} /></div>
              <div><Label>Reviewer</Label><Input value={revForm.reviewer || ""} onChange={(e) => setRevForm({ ...revForm, reviewer: e.target.value })} /></div>
              <div><Label>Overall Rating (1-5)</Label><Input type="number" min="1" max="5" step="0.1" value={revForm.overall_rating} onChange={(e) => setRevForm({ ...revForm, overall_rating: e.target.value })} /></div>
            </div>
            <div><Label>Strengths</Label><Textarea rows={2} value={revForm.strengths || ""} onChange={(e) => setRevForm({ ...revForm, strengths: e.target.value })} /></div>
            <div><Label>Areas to Improve</Label><Textarea rows={2} value={revForm.areas_to_improve || ""} onChange={(e) => setRevForm({ ...revForm, areas_to_improve: e.target.value })} /></div>
            <div><Label>Comments</Label><Textarea rows={2} value={revForm.comments || ""} onChange={(e) => setRevForm({ ...revForm, comments: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={revForm.status} onValueChange={(v) => setRevForm({ ...revForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="finalized">Finalized</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRevOpen(false)}>Cancel</Button><Button onClick={saveRev} data-testid="save-review-btn">{revEdit ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal Dialog */}
      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{goalEdit ? "Edit Goal" : "New Development Goal"}</DialogTitle><DialogDescription>Define a SMART goal with a target date and progress tracking.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Employee *</Label>
                <Select value={goalForm.employee_id} onValueChange={(v) => setGoalForm({ ...goalForm, employee_id: v })}>
                  <SelectTrigger data-testid="goal-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label>
                <Select value={goalForm.category} onValueChange={(v) => setGoalForm({ ...goalForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="skill">Skill</SelectItem><SelectItem value="career">Career</SelectItem><SelectItem value="project">Project</SelectItem><SelectItem value="behaviour">Behaviour</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Title *</Label><Input value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea rows={2} value={goalForm.description || ""} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Target Date</Label><Input type="date" value={goalForm.target_date || ""} onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })} /></div>
              <div><Label>Progress %</Label><Input type="number" min="0" max="100" value={goalForm.progress} onChange={(e) => setGoalForm({ ...goalForm, progress: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={goalForm.status} onValueChange={(v) => setGoalForm({ ...goalForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="achieved">Achieved</SelectItem><SelectItem value="missed">Missed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGoalOpen(false)}>Cancel</Button><Button onClick={saveGoal} data-testid="save-goal-btn">{goalEdit ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
